/*
 * Before/after divider.
 *
 * The visible control is an <input type="range"> laid over the frame, which
 * gives keyboard and screen-reader support for free. What it does not give is a
 * reliable drag on touch: with `appearance: none` and a transparent thumb, iOS
 * sets the value where you tap but only keeps tracking if the gesture started
 * on the thumb itself — which is invisible. Tapping moved the divider, dragging
 * did nothing.
 *
 * So the drag is handled here with Pointer Events, which behave the same for
 * mouse, touch and pen, and `setPointerCapture` keeps following the finger even
 * when it leaves the frame. The range stays in sync so the accessible value and
 * the arrow keys keep working.
 */
(() => {
  const init = (root) => {
    const range = root.querySelector('.pdp-ba__range');
    const frame = root.querySelector('.pdp-ba__frame');
    if (!range || !frame || range.dataset.baBound === 'true') return;
    range.dataset.baBound = 'true';

    const paint = (value) => root.style.setProperty('--ba-pos', `${value}%`);

    const setFromPointer = (clientX) => {
      const box = frame.getBoundingClientRect();
      if (!box.width) return;
      const percent = Math.min(100, Math.max(0, ((clientX - box.left) / box.width) * 100));
      range.value = String(percent);
      paint(percent);
    };

    let pointerId = null;

    root.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      pointerId = event.pointerId;
      // Capture so the drag survives the finger leaving the frame. A vertical
      // swipe is still the browser's: `touch-action: pan-y` lets it scroll and
      // cancel us, which `pointercancel` below handles.
      try { root.setPointerCapture(pointerId); } catch { /* older engines */ }
      setFromPointer(event.clientX);
    });

    root.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      event.preventDefault();
      setFromPointer(event.clientX);
    }, { passive: false });

    const release = (event) => {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      try { root.releasePointerCapture(pointerId); } catch { /* already gone */ }
      pointerId = null;
    };
    root.addEventListener('pointerup', release);
    root.addEventListener('pointercancel', release);
    root.addEventListener('lostpointercapture', release);

    // Keyboard, and any other change to the input, still drives the divider.
    range.addEventListener('input', () => paint(range.value));
    paint(range.value);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('[data-before-after]').forEach(init);
    if (scope.matches?.('[data-before-after]')) init(scope);
  };

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('wowthings:content:load', (event) => initAll(event.target));
  initAll();
})();
